// Picks the fixture-backed or real HTTP TokenUsageClient implementation.
//
// Default is the real HTTP client (matches production behavior). Set
// VITE_TOKENUSAGE_USE_FIXTURES=true locally to develop/test this page
// against realistic mock data without hitting the live backend.
//
// Two independent guards keep this dev/offline aid out of production (ADO
// thread 975046):
//  1. `resolveUseFixtures` also requires `import.meta.env.DEV`, which Vite
//     derives from the build *mode* (always "production" for a real
//     `vite build`), not from any VITE_-prefixed env var — so a stray or
//     mistaken VITE_TOKENUSAGE_USE_FIXTURES=true in a production config can
//     never route a real user to mock data.
//  2. The fixture module is loaded via a dynamic `import()` behind that same
//     DEV-only branch, so in a production build the branch (and therefore
//     the fixtures module and its data) is dead-code-eliminated out of the
//     bundle entirely instead of always being bundled.
//
// `tokenusage.fixtures.ts` and this selection logic are an optional
// dev/offline aid and can be removed once no longer needed. See
// `tokenusage.ts` for the deployed REST contract this client implements.
import { createTokenUsageClient } from './tokenusage';
import type { TokenUsageClient, TokenProvider } from './tokenusage';

/**
 * Pure decision logic, factored out so it's testable without having to flip
 * Vite's actual build mode: a real `vite build` always has `isDev === false`
 * regardless of which VITE_-prefixed env vars happen to be set.
 */
export function resolveUseFixtures(isDev: boolean, rawFlag: string | undefined): boolean {
  if (!isDev) return false;
  return String(rawFlag).trim().toLowerCase() === 'true';
}

function shouldUseFixtures(): boolean {
  return resolveUseFixtures(import.meta.env.DEV, import.meta.env.VITE_TOKENUSAGE_USE_FIXTURES);
}

export async function createTokenUsageClientFor(getToken: TokenProvider): Promise<TokenUsageClient> {
  if (shouldUseFixtures()) {
    const { createFixtureTokenUsageClient } = await import('./tokenusage.fixtures');
    return createFixtureTokenUsageClient();
  }
  return createTokenUsageClient(getToken);
}

export type { TokenUsageClient } from './tokenusage';
